/**
 * AdminBillingPage — Billing subscriptions and invoices overview.
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { AdminConsoleService } from '../../../services/adminConsoleService';
import { RequireCapability } from '../../../components/auth/RequireCapability';
import { AdminConsoleLayout } from '../components/AdminConsoleLayout';
import { ModuleCard, ModuleLoadingState } from '../../shared/components/ModulePageHeader';

const currency = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

const PLAN_PRICES: Record<string, number> = {
  starter: 299,
  professional: 599,
  enterprise: 1499,
};

const statusBadge: Record<string, string> = {
  active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  trial: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  suspended: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  expired: 'bg-slate-100 text-slate-600 dark:bg-zinc-800 dark:text-zinc-400',
  paid: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  failed: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
};

type BillingView = 'subscriptions' | 'invoices';

interface AdminBillingPageProps {
  externalView?: BillingView;
}

export const AdminBillingPage: React.FC<AdminBillingPageProps> = ({ externalView }) => {
  const view = externalView ?? 'subscriptions';

  const subscriptionsQuery = useQuery({
    queryKey: ['admin-billing-subscriptions'],
    queryFn: () => AdminConsoleService.getBillingSubscriptions(),
  });

  const invoicesQuery = useQuery({
    queryKey: ['admin-billing-invoices'],
    queryFn: () => AdminConsoleService.getBillingInvoices(),
    enabled: view === 'invoices',
  });

  const subs = subscriptionsQuery.data ?? [];
  const invoices = invoicesQuery.data ?? [];
  const totalMrr = subs.filter((s) => s.status === 'active').reduce((acc, s) => acc + s.monthly_value, 0);
  const totalArr = totalMrr * 12;

  return (
    <RequireCapability capability="admin.access" realRoleOnly>
      <AdminConsoleLayout>
        <div className="enterprise-page h-full overflow-y-auto">

          {/* Summary KPIs */}
          <div className="grid grid-cols-3 gap-[var(--block-gap)]">
            <div className="enterprise-card-surface p-[var(--card-padding)]">
              <p className="enterprise-field-label">MRR Total</p>
              <p className="mt-1 text-xl font-semibold enterprise-text-primary tabular-nums font-mono">
                {currency.format(totalMrr)}
              </p>
            </div>
            <div className="enterprise-card-surface p-[var(--card-padding)]">
              <p className="enterprise-field-label">ARR (Estimado)</p>
              <p className="mt-1 text-xl font-semibold enterprise-text-primary tabular-nums font-mono">
                {currency.format(totalArr)}
              </p>
            </div>
            <div className="enterprise-card-surface p-[var(--card-padding)]">
              <p className="enterprise-field-label">Assinaturas Ativas</p>
              <p className="mt-1 text-xl font-semibold enterprise-text-primary tabular-nums">
                {subs.filter((s) => s.status === 'active').length}
              </p>
            </div>
          </div>

          {/* Subscriptions Table */}
          {view === 'subscriptions' && (
            <>
              {subscriptionsQuery.isPending && <ModuleLoadingState label="Carregando assinaturas" />}
              {!subscriptionsQuery.isPending && (
                <ModuleCard title={`Assinaturas (${subs.length})`}>
                  {!subs.length ? (
                    <p className="text-[12px] enterprise-text-secondary py-4 text-center">Nenhuma assinatura encontrada.</p>
                  ) : (
                    <div className="enterprise-table-shell">
                      <table className="w-full text-left text-sm">
                        <thead className="enterprise-table-header">
                          <tr>
                            <th className="enterprise-table-head-cell">Cliente</th>
                            <th className="enterprise-table-head-cell">Plano</th>
                            <th className="enterprise-table-head-cell">Ciclo</th>
                            <th className="enterprise-table-head-cell text-right">Valor Mensal</th>
                            <th className="enterprise-table-head-cell">Status</th>
                            <th className="enterprise-table-head-cell">Início</th>
                            <th className="enterprise-table-head-cell">Trial até</th>
                          </tr>
                        </thead>
                        <tbody>
                          {subs.map((sub) => (
                            <tr key={sub.id} className="enterprise-table-row">
                              <td className="enterprise-table-cell font-medium">{sub.organization_name || sub.organization_id}</td>
                              <td className="enterprise-table-cell capitalize">{sub.plan}</td>
                              <td className="enterprise-table-cell capitalize">{sub.billing_cycle === 'monthly' ? 'Mensal' : 'Anual'}</td>
                              <td className="enterprise-table-cell text-right tabular-nums font-mono">
                                {currency.format(sub.monthly_value)}
                              </td>
                              <td className="enterprise-table-cell">
                                <span className={`inline-block rounded px-2 py-0.5 text-[11px] font-medium ${statusBadge[sub.status] ?? ''}`}>
                                  {sub.status.toUpperCase()}
                                </span>
                              </td>
                              <td className="enterprise-table-cell enterprise-text-secondary">
                                {sub.started_at ? new Date(sub.started_at).toLocaleDateString('pt-BR') : '—'}
                              </td>
                              <td className="enterprise-table-cell enterprise-text-secondary">
                                {sub.trial_ends_at ? new Date(sub.trial_ends_at).toLocaleDateString('pt-BR') : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </ModuleCard>
              )}
            </>
          )}

          {/* Invoices Table */}
          {view === 'invoices' && (
            <>
              {invoicesQuery.isPending && <ModuleLoadingState label="Carregando faturas" />}
              {!invoicesQuery.isPending && (
                <ModuleCard title={`Faturas (${invoices.length})`}>
                  {!invoices.length ? (
                    <p className="text-[12px] enterprise-text-secondary py-4 text-center">
                      Nenhuma fatura encontrada. A tabela de pagamentos pode não estar configurada.
                    </p>
                  ) : (
                    <div className="enterprise-table-shell">
                      <table className="w-full text-left text-sm">
                        <thead className="enterprise-table-header">
                          <tr>
                            <th className="enterprise-table-head-cell">Cliente</th>
                            <th className="enterprise-table-head-cell text-right">Valor</th>
                            <th className="enterprise-table-head-cell">Status</th>
                            <th className="enterprise-table-head-cell">Emissão</th>
                            <th className="enterprise-table-head-cell">Vencimento</th>
                            <th className="enterprise-table-head-cell">Pagamento</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoices.map((inv) => (
                            <tr key={inv.id} className="enterprise-table-row">
                              <td className="enterprise-table-cell font-medium">{inv.organization_name || inv.organization_id}</td>
                              <td className="enterprise-table-cell text-right tabular-nums font-mono">{currency.format(inv.amount)}</td>
                              <td className="enterprise-table-cell">
                                <span className={`inline-block rounded px-2 py-0.5 text-[11px] font-medium ${statusBadge[inv.status] ?? ''}`}>
                                  {inv.status.toUpperCase()}
                                </span>
                              </td>
                              <td className="enterprise-table-cell enterprise-text-secondary">
                                {new Date(inv.issued_at).toLocaleDateString('pt-BR')}
                              </td>
                              <td className="enterprise-table-cell enterprise-text-secondary">
                                {new Date(inv.due_date).toLocaleDateString('pt-BR')}
                              </td>
                              <td className="enterprise-table-cell enterprise-text-secondary">
                                {inv.paid_at ? new Date(inv.paid_at).toLocaleDateString('pt-BR') : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </ModuleCard>
              )}
            </>
          )}
        </div>
      </AdminConsoleLayout>
    </RequireCapability>
  );
};
